import { CurrencyPipe } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PaymentUi, StripeApiService } from '@anduin/api';
import { extractFailureContext, GenericErrorComponent, Logger, NULL_DATA_STRING, TrackService } from '@anduin/shared';
import { NgxSpinnerService } from 'ngx-spinner';
import { take } from 'rxjs/operators';
import { PaymentHistoryTrackService } from '../../tracking/payment-history-tracking.service';
import { resolvePaymentMethodType } from '../../tracking/payment-history-tracking.utils';
import { PaymentsHistoryStep } from '../../tracking/payment-history-workflow.models';
import { PaymentRefundSuccessModalComponent } from '../payment-refund-success-modal/payment-refund-success-modal.component';

export interface PaymentRefundDialogData extends PaymentUi {
  engagement_names?: string | null;
  payment_ar_tran_id?: number;
}

const PREPAYMENT_REFUND_TRIGGERED_EVENT = 'PrepaymentRefundTriggered';

@Component({
  selector: 'payment-refund-confirm-modal',
  templateUrl: './payment-refund-confirm-modal.component.html',
  styleUrls: ['./payment-refund-confirm-modal.component.scss'],
  standalone: false,
})
export class PaymentRefundConfirmModalComponent {
  private readonly paymentHistoryTracking = inject(PaymentHistoryTrackService);

  private submitted = false;

  constructor(
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentRefundDialogData,
    private stripeApiService: StripeApiService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private readonly currencyPipe: CurrencyPipe,
    private readonly trackService: TrackService,
    private readonly logger: Logger,
  ) {
    // Any close without SUBMITTED is treated as user-cancel so analytics can distinguish
    // abandoned refunds from completed attempts. REFUND_FAILED auto-closes the dialog and is itself
    // terminal — keep `submitted` true on failure so the auto-close doesn't double-emit CANCELLED.
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.submitted) {
          this.paymentHistoryTracking.trackWorkflow(
            PaymentsHistoryStep.REFUND_CANCELLED,
            this.buildRefundContext(this.data),
          );
        }
      });
  }

  get currencyCode(): string {
    return this.data.currency_code || 'USD';
  }

  /**
   * Close the modal.
   */
  onClose(): void {
    this.dialogRef.close(false);
  }

  /**
   * confirm refund payment and display error or success modal
   * @param payment
   */
  confirmRefund(payment: PaymentRefundDialogData) {
    this.submitted = true;
    const refundContext = this.buildRefundContext(payment);
    const startedAt = performance.now();
    this.paymentHistoryTracking.trackWorkflow(PaymentsHistoryStep.REFUND_SUBMITTED, refundContext);

    try {
      this.trackPrepaymentRefundIfApplicable(payment);
    } catch (error) {
      this.logger.warn('TRACKING :: Track prepayment payment refund event failed', { event: PREPAYMENT_REFUND_TRIGGERED_EVENT, error });
    }

    this.spinner.show();
    this.stripeApiService.createRefund(payment).subscribe(
      (paymentId: string) => {
        // Hide the spinner once call completes.
        this.spinner.hide();
        this.paymentHistoryTracking.trackWorkflow(PaymentsHistoryStep.REFUND_SUCCEEDED, {
          ...refundContext,
          operationDurationMs: performance.now() - startedAt,
        });
        this.dialog.open(PaymentRefundSuccessModalComponent);
        console.log('Refund Payment Successful with reference: ', paymentId);
        this.dialogRef.close(true);
      },
      (error) => {
        // If there is an error hide the spinner.
        this.spinner.hide();
        this.paymentHistoryTracking.trackWorkflow(PaymentsHistoryStep.REFUND_FAILED, {
          ...refundContext,
          ...extractFailureContext(error),
          operationDurationMs: performance.now() - startedAt,
        });
        this.dialogRef.close(false);
        console.error('Refund Payment Errored with reference: ', payment.paymentPPId);
        this.dialog.open(GenericErrorComponent);
      },
    );
  }

  private buildRefundContext(payment: PaymentRefundDialogData): {
    paymentId: number | null;
    amount: number;
    paymentMethodType: string;
  } {
    return {
      paymentId: payment.payment_ar_tran_id ?? null,
      amount: payment.amount,
      paymentMethodType: resolvePaymentMethodType(payment.paymentMethod),
    };
  }

  // TODO(FIRM-11196): Remove legacy 'PrepaymentRefundTriggered' emit after one-release migration window.
  private trackPrepaymentRefundIfApplicable(payment: PaymentRefundDialogData): void {
    if (!payment || !this.isAllocatedToPrepayment(payment)) {
      return;
    }

    this.trackService.track(PREPAYMENT_REFUND_TRIGGERED_EVENT, {
      paymentArTranId: payment.payment_ar_tran_id,
      paymentMethod: payment.paymentMethod,
    });
  }

  private isAllocatedToPrepayment(payment: PaymentRefundDialogData): boolean {
    const engagementNames = payment.engagement_names;
    return !!engagementNames && engagementNames !== NULL_DATA_STRING;
  }
}
